import { TooltipProvider } from "@/components/ui/tooltip";
import { CalendarDay } from "@/components/CalendarDay";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { isToday } from "date-fns";
import { memo, useMemo } from "react";
import { EventInfo, Volatility } from "./CalendarDay";

/**
 * Interface for a single calendar day cell data
 */
export interface CalendarDayInfo {
  date: number;
  events: EventInfo;
  isLastMonth?: boolean;
  disabled?: boolean;
}

/**
 * Props for the CalendarGrid component
 */
export interface CalendarGridProps {
  days: CalendarDayInfo[];
  onDateClick?: (date: number) => void;
  currentMonth: Date;
  isLoading?: boolean;
}

/**
 * Maps weekday index to labels - using consistent format
 */
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Calendar Grid Component
 * Renders a month view grid of calendar days with enhanced responsive design
 */
export const CalendarGrid = memo(({ 
  days, 
  onDateClick, 
  currentMonth, 
  isLoading = false 
}: CalendarGridProps) => {
  const isMobile = useIsMobile();
  
  // Use consistent day labels
  const activeDayLabels = WEEKDAY_LABELS;

  /**
   * Memoized function to check if each day is the current day
   */
  const isTodayMap = useMemo(() => {
    // Create a map of date numbers to boolean indicating if it's today
    const result: Record<number, boolean> = {};
    
    days.forEach(day => {
      if (day.date === 0) return;
      
      const cellDate = new Date(
        currentMonth.getFullYear(), 
        currentMonth.getMonth(), 
        day.date
      );
      
      result[day.date] = isToday(cellDate);
    });
    
    return result;
  }, [days, currentMonth]);

  /**
   * Function to check if a date is from the current month
   */
  const isCurrentMonthDate = (day: CalendarDayInfo): boolean => {
    return !day.isLastMonth && day.date !== 0;
  };

  /**
   * Render a weekday header with enhanced styling
   */
  const renderWeekdayHeader = (day: string, index: number) => (
    <div
      key={day}
      className={cn(
        // Base styles with improved typography and spacing
        "py-2 md:py-3 text-center relative",
        "bg-gradient-to-b from-slate-800/60 via-slate-800/40 to-slate-900/60",
        "border-b border-slate-700/40",
        "border-r border-slate-700/30 last:border-r-0",
        "transition-all duration-200",
        
        // Enhanced text styling based on day type - more subtle weekend styling
        index === 0 || index === 6 
          ? "text-slate-300/90 font-semibold bg-gradient-to-b from-slate-900/70 to-slate-900/90" 
          : "text-slate-200 font-medium",
        
        // Hover effects for interactivity  
        "hover:bg-slate-700/20"
      )}
    >
      {isLoading ? (
        <Skeleton className="h-4 w-8 mx-auto bg-slate-700/50" />
      ) : (
        <div className="flex flex-col items-center">
          {/* Single day label - responsive */}
          <span className={cn(
            "font-semibold tracking-wide transition-colors duration-150",
            "text-sm md:text-base",
            index === 0 || index === 6 ? "text-slate-300" : "text-slate-100"
          )}>
            {day}
          </span>
          
          {/* Removed accent line for cleaner professional look */}
        </div>
      )}
    </div>
  );

  /**
   * Render a calendar day cell
   */
  const renderDayCell = (day: CalendarDayInfo, index: number) => (
    <CalendarDay 
      key={`day-${index}`}
      date={day.date} 
      events={day.events}
      isLastMonth={day.isLastMonth}
      isCurrentMonth={isCurrentMonthDate(day)}
      isToday={isTodayMap[day.date] || false}
      onClick={onDateClick}
      currentMonth={currentMonth}
      isLoading={isLoading}
      disabled={day.disabled}
    />
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn(
        // Enhanced container with better spacing and visual hierarchy
        "relative",
        "p-2 sm:p-3 md:p-6 lg:p-8 pb-3 sm:pb-4 md:pb-6 lg:pb-8",
        "bg-gradient-to-b from-slate-900/20 via-transparent to-slate-900/40"
      )}>
        {/* Subtle background pattern for visual interest */}
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
        
        <div className={cn(
          // Main grid container with enhanced styling
          "relative grid grid-cols-7 gap-0 overflow-hidden",
          "rounded-xl lg:rounded-2xl",
          "border border-slate-700/40",
          "shadow-2xl shadow-black/25",
          "bg-gradient-to-br from-slate-800/30 via-slate-900/40 to-black/50",
          "backdrop-blur-sm"
        )}>
          {/* Enhanced weekday headers */}
          {activeDayLabels.map(renderWeekdayHeader)}

          {/* Calendar grid cells */}
          {days.map(renderDayCell)}
          
          {/* Subtle inner glow effect */}
          <div className="absolute inset-0 rounded-xl lg:rounded-2xl pointer-events-none bg-gradient-to-br from-white/5 via-transparent to-transparent" />
        </div>
      </div>
    </TooltipProvider>
  );
});

CalendarGrid.displayName = 'CalendarGrid';
